import { useEffect, useState, useCallback, Fragment } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PageHeader, SectionCard, StatCard, EmptyState } from "../components/ui/kaizen";
import { CalendarBlank, Megaphone, CheckCircle, Trophy, Plus, Trash, Copy, ArrowsClockwise, LinkSimple, Sparkle, ListChecks, Circle, CaretDown, CaretRight, ChartBar } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const SAISONS = [["automne", "Automne"], ["hiver", "Hiver"], ["printemps", "Printemps"], ["ete", "Été"]];
const TYPES = [
  ["reprise_cours", "Reprise des cours"], ["promotion", "Promotion"], ["promotion_fetes", "Promo pré-Fêtes"],
  ["retour_apres_fetes", "Retour après Fêtes"], ["essais_gratuits", "Essais gratuits"], ["jeux_martiaux", "Jeux Martiaux"],
  ["competition_externe", "Compétition externe"], ["financement", "Financement"], ["camp_ete", "Camp d'été"],
  ["commanditaires", "Commanditaires"], ["ambassadeurs", "Ambassadeurs"], ["contenu_blog", "Contenu / Blogue"],
];
const STATUTS = [
  ["brouillon", "Brouillon"], ["planifiee", "Planifiée"], ["production", "Production"], ["a_approuver", "À approuver"],
  ["approuvee", "Approuvée"], ["programmee", "Programmée"], ["active", "Active"], ["terminee", "Terminée"], ["annulee", "Annulée"],
];
const typeLabel = (t) => (TYPES.find((x) => x[0] === t) || [t, t])[1];
const statutLabel = (s) => (STATUTS.find((x) => x[0] === s) || [s, s])[1];
const statutColor = (s) => ({
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  programmee: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  a_approuver: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  terminee: "bg-zinc-700/40 text-zinc-400 border-zinc-700",
  annulee: "bg-red-500/15 text-red-300 border-red-500/30",
}[s] || "bg-zinc-800 text-zinc-300 border-zinc-700");

const inputCls = "w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-500 outline-none";
const labelCls = "block text-xs font-semibold text-zinc-400 mb-1";

export default function PlanificateurAnnuelModule() {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editRes, setEditRes] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, an] = await Promise.all([
        api.get(`/planner/overview?year=${year}`),
        api.get(`/planner/analytics?year=${year}`),
      ]);
      setData(ov.data); setPlan(ov.data.annual_plan); setAnalytics(an.data);
    } catch (e) {
      toast.error("Chargement impossible", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const saveResults = async () => {
    try {
      await api.patch(`/campaigns/${editRes.id}`, { resultats: editRes.resultats });
      toast.success("Résultats enregistrés");
      setEditRes(null); await load();
    } catch (e) { toast.error("Enregistrement impossible"); }
  };
  const setRes = (k, v) => setEditRes((e) => ({ ...e, resultats: { ...e.resultats, [k]: Number(v) || 0 } }));
  const [openTasks, setOpenTasks] = useState(null);
  const [tasksMap, setTasksMap] = useState({});

  const loadTasks = async (cid) => {
    try {
      const { data } = await api.get(`/campaigns/${cid}/tasks`);
      setTasksMap((m) => ({ ...m, [cid]: data }));
    } catch (e) { /* ignore */ }
  };
  const toggleTasks = (cid) => {
    if (openTasks === cid) { setOpenTasks(null); return; }
    setOpenTasks(cid);
    if (!tasksMap[cid]) loadTasks(cid);
  };
  const generateTasks = async (cid) => {
    try { await api.post(`/campaigns/${cid}/generate-tasks`); await loadTasks(cid); toast.success("Échéances générées (J-60…J+7)"); }
    catch (e) { toast.error("Génération impossible", { description: e?.response?.data?.detail || "Définis d'abord une date de début." }); }
  };
  const toggleTaskDone = async (cid, task) => {
    try {
      await api.patch(`/campaign-tasks/${task.id}`, { done: !task.done });
      setTasksMap((m) => ({ ...m, [cid]: m[cid].map((t) => t.id === task.id ? { ...t, done: !t.done } : t) }));
    } catch (e) { toast.error("Mise à jour impossible"); }
  };

  const savePlan = async () => {
    setSavingPlan(true);
    try {
      await api.put("/annual-plan", { ...plan, annee: year });
      toast.success("Plan annuel enregistré");
      await load();
    } catch (e) { toast.error("Enregistrement impossible"); }
    finally { setSavingPlan(false); }
  };

  const duplicatePlan = async () => {
    try {
      await api.post("/annual-plan/duplicate", { from_year: year, to_year: year + 1 });
      toast.success(`Plan dupliqué vers ${year + 1}`, { description: "Les dates ont été décalées d'un an." });
      setYear(year + 1);
    } catch (e) { toast.error("Duplication impossible", { description: e?.response?.data?.detail || "" }); }
  };

  const openNew = (tpl) => {
    setForm({
      nom: tpl ? `${tpl.nom} ${year}` : "",
      type: tpl?.type || "promotion",
      saison: tpl?.saison || "automne",
      statut: "brouillon",
      objectif: tpl?.objectif || "",
      objectif_inscriptions: 0,
      audience: tpl?.audience || "",
      offre: "",
      date_debut: "",
      date_fin: "",
      lien_inscription: "",
      template_id: tpl?.id || null,
      annee: year,
    });
    setShowForm(true);
  };

  const createCampaign = async () => {
    if (!form.nom.trim()) { toast.error("Donne un nom à la campagne."); return; }
    setSaving(true);
    try {
      await api.post("/campaigns", form);
      toast.success("Campagne créée");
      setShowForm(false); setForm(null);
      await load();
    } catch (e) { toast.error("Création impossible", { description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  const setStatut = async (c, statut) => {
    try { await api.patch(`/campaigns/${c.id}`, { statut }); await load(); }
    catch (e) { toast.error("Mise à jour impossible"); }
  };
  const delCampaign = async (c) => {
    if (!window.confirm(`Supprimer la campagne « ${c.nom} » ?`)) return;
    try { await api.delete(`/campaigns/${c.id}`); toast.success("Campagne supprimée"); await load(); }
    catch (e) { toast.error("Suppression impossible"); }
  };

  const exportCampaignPdf = async (cid) => {
    try {
      const res = await api.get(`/planner/campaign-rollup/pdf?campaign_id=${cid}`, { responseType: "blob" });
      const href = URL.createObjectURL(res.data); window.open(href, "_blank");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch { toast.error("Export PDF impossible"); }
  };
  const autoResults = async (cid) => {
    try {
      const { data } = await api.post(`/campaigns/${cid}/auto-results`);
      toast.success(`Résultats CRM : ${data.resultats.prospects} prospects · ${data.resultats.essais} essais`);
      await load();
    } catch (e) { toast.error("Auto-résultats impossible", { description: e?.response?.data?.detail || "Définis d'abord les dates." }); }
  };

  const setP = (k, v) => setPlan((p) => ({ ...p, [k]: v }));
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading && !data) return <div className="p-8 text-zinc-400" data-testid="planner-loading">Chargement…</div>;
  const c = data?.counts || {};

  return (
    <div className="pb-16" data-testid="planificateur-annuel-page">
      <PageHeader
        title="🗓️ Planificateur annuel & Centre de campagnes"
        subtitle="Le centre de contrôle des campagnes, événements et échéances de l'année."
        icon={CalendarBlank}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setYear(year - 1)} className="px-3 py-2 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700" data-testid="year-prev">‹</button>
            <span className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 font-bold text-lg" data-testid="year-label">{year}</span>
            <button onClick={() => setYear(year + 1)} className="px-3 py-2 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700" data-testid="year-next">›</button>
            <button onClick={load} className="ml-1 px-3 py-2 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700 flex items-center gap-2" data-testid="planner-refresh"><ArrowsClockwise size={15} /></button>
          </div>
        }
      />

      <div className="px-8 lg:px-12 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Campagnes" value={c.campaigns ?? 0} icon={Megaphone} />
          <StatCard label="Actives / planifiées" value={c.active ?? 0} icon={CheckCircle} />
          <StatCard label="Événements à venir" value={c.upcoming_events ?? 0} icon={Trophy} />
          <StatCard label="À approuver" value={data?.approval_pending ?? 0} icon={Sparkle} />
        </div>

        {/* Meilleure campagne de l'année (revenu / inscription) */}
        {(() => {
          const cands = (data?.campaigns || [])
            .map((cp) => {
              const r = cp.resultats || {};
              const insc = Number(r.inscriptions) || 0;
              const rev = Number(r.revenus) || 0;
              return insc > 0 ? { cp, insc, rev, rpi: rev / insc } : null;
            })
            .filter(Boolean)
            .sort((a, b) => b.rpi - a.rpi);
          if (cands.length === 0) return null;
          const best = cands[0];
          return (
            <div data-testid="best-campaign-card"
              className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-emerald-500/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1.5">
                  <Trophy size={14} weight="fill" /> Campagne la plus rentable {year}
                </div>
                <div className="text-lg font-bold text-zinc-100 mt-1">{best.cp.nom}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{typeLabel(best.cp.type)} · {best.insc} inscription(s) · {best.rev.toLocaleString("fr-CA")} $ de revenus</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-emerald-300">{best.rpi.toLocaleString("fr-CA", { maximumFractionDigits: 0 })} $</div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Revenu / inscription</div>
              </div>
            </div>
          );
        })()}

        {/* Plan annuel */}
        <SectionCard title={`Plan annuel — ancres de dates ${year}`} icon={CalendarBlank}
          headerActions={
            <div className="flex items-center gap-2">
              <button onClick={savePlan} disabled={savingPlan} data-testid="save-annual-plan"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#D90429] text-white hover:bg-[#b8031f] disabled:opacity-50">
                {savingPlan ? "…" : "Enregistrer"}
              </button>
              <button onClick={duplicatePlan} data-testid="duplicate-annual-plan"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-200 border border-zinc-700 hover:bg-zinc-700 flex items-center gap-2">
                <Copy size={15} /> Dupliquer → {year + 1}
              </button>
            </div>
          }>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[["reprise_automne", "Reprise Automne"], ["reprise_hiver", "Reprise Hiver"], ["reprise_printemps", "Reprise Printemps"],
              ["reprise_ete", "Reprise Été"], ["pause_fetes_debut", "Début pause des Fêtes"], ["pause_fetes_fin", "Fin pause des Fêtes"]].map(([k, lbl]) => (
              <div key={k}>
                <label className={labelCls}>{lbl}</label>
                <input type="date" value={(plan?.[k] || "").slice(0, 10)} onChange={(e) => setP(k, e.target.value)} className={inputCls} data-testid={`plan-${k}`} />
              </div>
            ))}
          </div>
          <div className="mt-3">
            <label className={labelCls}>Notes / dates critiques à confirmer</label>
            <textarea value={plan?.notes || ""} onChange={(e) => setP("notes", e.target.value)} rows={2} className={inputCls} placeholder="Ex : confirmer date Jeux Martiaux, réserver salle…" data-testid="plan-notes" />
          </div>
        </SectionCard>

        {/* Modèles → créer une campagne */}
        <SectionCard title="Modèles de campagne" icon={Sparkle}
          headerActions={
            <button onClick={() => openNew(null)} data-testid="new-campaign-btn"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-100 border border-zinc-700 hover:bg-zinc-700 flex items-center gap-2">
              <Plus size={15} /> Campagne vierge
            </button>
          }>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(data?.templates || []).map((t) => (
              <button key={t.id} onClick={() => openNew(t)} data-testid={`template-${t.type}`}
                className="text-left p-3 rounded-lg bg-zinc-800/60 border border-zinc-700 hover:border-[#D90429] hover:bg-zinc-800 transition-colors group">
                <div className="text-xs font-bold text-zinc-100">{t.nom}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{typeLabel(t.type)} · prép. {t.prep_days}j</div>
                <div className="text-[11px] text-[#D90429] mt-2 font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"><Plus size={11} /> Créer</div>
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Formulaire nouvelle campagne */}
        {showForm && form && (
          <SectionCard title="Nouvelle campagne" icon={Megaphone}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>Nom *</label><input value={form.nom} onChange={(e) => setF("nom", e.target.value)} className={inputCls} data-testid="form-nom" /></div>
              <div><label className={labelCls}>Type</label><select value={form.type} onChange={(e) => setF("type", e.target.value)} className={inputCls} data-testid="form-type">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              <div><label className={labelCls}>Saison</label><select value={form.saison} onChange={(e) => setF("saison", e.target.value)} className={inputCls} data-testid="form-saison">{SAISONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              <div><label className={labelCls}>Date de début</label><input type="date" value={form.date_debut} onChange={(e) => setF("date_debut", e.target.value)} className={inputCls} data-testid="form-date-debut" /></div>
              <div><label className={labelCls}>Date de fin</label><input type="date" value={form.date_fin} onChange={(e) => setF("date_fin", e.target.value)} className={inputCls} data-testid="form-date-fin" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Objectif</label><input value={form.objectif} onChange={(e) => setF("objectif", e.target.value)} className={inputCls} data-testid="form-objectif" /></div>
              <div><label className={labelCls}>Objectif inscriptions (nb)</label><input type="number" min={0} value={form.objectif_inscriptions} onChange={(e) => setF("objectif_inscriptions", parseInt(e.target.value || "0", 10))} className={inputCls} data-testid="form-objectif-inscriptions" placeholder="Ex : 20" /><span className="text-[10px] text-zinc-500">Déclenche une alerte courriel à mi-parcours.</span></div>
              <div><label className={labelCls}>Offre</label><input value={form.offre} onChange={(e) => setF("offre", e.target.value)} className={inputCls} placeholder="Ex : 1er mois gratuit" /></div>
              <div><label className={labelCls}>Lien d'inscription</label><input value={form.lien_inscription} onChange={(e) => setF("lien_inscription", e.target.value)} className={inputCls} placeholder="https://" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createCampaign} disabled={saving} data-testid="submit-campaign" className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#D90429] text-white hover:bg-[#b8031f] disabled:opacity-50">{saving ? "Création…" : "Créer la campagne"}</button>
              <button onClick={() => { setShowForm(false); setForm(null); }} className="px-5 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700">Annuler</button>
            </div>
          </SectionCard>
        )}

        {/* Campagnes de l'année */}
        <SectionCard title={`Campagnes ${year}`} icon={Megaphone}>
          {(!data?.campaigns || data.campaigns.length === 0) ? (
            <EmptyState icon={Megaphone} title="Aucune campagne cette année" description="Crée une campagne depuis un modèle ci-dessus pour démarrer." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="campaigns-table">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase border-b border-zinc-800">
                    <th className="py-2 pr-3">Campagne</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Saison</th>
                    <th className="py-2 pr-3">Dates</th><th className="py-2 pr-3">Statut</th><th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns.map((cp) => (
                    <Fragment key={cp.id}>
                    <tr className="border-b border-zinc-800/60" data-testid={`campaign-row-${cp.id}`}>
                      <td className="py-2.5 pr-3 font-medium text-zinc-100">
                        <button onClick={() => toggleTasks(cp.id)} className="inline-flex items-center gap-1 hover:text-[#D90429]" data-testid={`toggle-tasks-${cp.id}`}>
                          {openTasks === cp.id ? <CaretDown size={13} /> : <CaretRight size={13} />} {cp.nom}
                        </button>
                        <div className="text-[11px] text-zinc-500 pl-4">{cp.objectif}</div>
                        <div className="flex flex-wrap items-center gap-1.5 pl-4 mt-1" data-testid={`campaign-results-${cp.id}`}>
                          {(() => {
                            const r = cp.resultats || {};
                            const lk = (analytics?.linked_content || {})[cp.id] || {};
                            const linkedTotal = Object.values(lk).reduce((a, b) => a + (Number(b) || 0), 0);
                            const badges = [
                              ["prospects", r.prospects, "text-sky-300 bg-sky-500/10 border-sky-500/20"],
                              ["essais", r.essais, "text-amber-300 bg-amber-500/10 border-amber-500/20"],
                              ["inscr.", r.inscriptions, "text-emerald-300 bg-emerald-500/10 border-emerald-500/20"],
                              ["revenus $", r.revenus, "text-emerald-200 bg-emerald-600/10 border-emerald-600/20"],
                            ].filter(([, v]) => Number(v) > 0);
                            if (badges.length === 0 && linkedTotal === 0) return null;
                            return (
                              <>
                                {badges.map(([lbl, v, cls]) => (
                                  <span key={lbl} className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
                                    {lbl === "revenus $" ? `${Number(v).toLocaleString("fr-CA")} $` : `${v} ${lbl}`}
                                  </span>
                                ))}
                                {linkedTotal > 0 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded border text-violet-300 bg-violet-500/10 border-violet-500/20">
                                    🔗 {linkedTotal} contenu(s) lié(s)
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 text-zinc-300">{typeLabel(cp.type)}</td>
                      <td className="py-2.5 pr-3 text-zinc-400 capitalize">{cp.saison}</td>
                      <td className="py-2.5 pr-3 text-zinc-400">{cp.date_debut ? cp.date_debut.slice(0, 10) : "—"}{cp.date_fin ? ` → ${cp.date_fin.slice(0, 10)}` : ""}</td>
                      <td className="py-2.5 pr-3">
                        <select value={cp.statut} onChange={(e) => setStatut(cp, e.target.value)} data-testid={`campaign-status-${cp.id}`}
                          className={`text-[11px] font-semibold px-2 py-1 rounded-full border cursor-pointer outline-none ${statutColor(cp.statut)}`}>
                          {STATUTS.map(([v, l]) => <option key={v} value={v} className="bg-zinc-900 text-zinc-100">{l}</option>)}
                        </select>
                      </td>
                      <td className="py-2.5 pr-3 text-right whitespace-nowrap">
                        <button onClick={() => autoResults(cp.id)} data-testid={`auto-results-${cp.id}`} title="Auto-remplir prospects/essais depuis le CRM" className="p-1.5 rounded text-zinc-400 hover:text-sky-300 hover:bg-zinc-800 mr-1"><ArrowsClockwise size={15} /></button>
                        <button onClick={() => exportCampaignPdf(cp.id)} data-testid={`export-pdf-${cp.id}`} title="Exporter le bilan PDF" className="p-1.5 rounded text-zinc-400 hover:text-red-300 hover:bg-zinc-800 mr-1"><Copy size={15} /></button>
                        <button onClick={() => setEditRes({ id: cp.id, nom: cp.nom, resultats: { prospects: 0, essais: 0, inscriptions: 0, revenus: 0, depenses: 0, ...(cp.resultats || {}) } })} data-testid={`results-btn-${cp.id}`} title="Saisir les résultats" className="p-1.5 rounded text-zinc-400 hover:text-emerald-300 hover:bg-zinc-800 mr-1"><ChartBar size={15} /></button>
                        <button onClick={() => toggleTasks(cp.id)} data-testid={`echeances-btn-${cp.id}`} title="Échéances J-60…J+7" className="p-1.5 rounded text-zinc-400 hover:text-amber-300 hover:bg-zinc-800 mr-1"><ListChecks size={15} /></button>
                        <button onClick={() => delCampaign(cp)} data-testid={`delete-campaign-${cp.id}`} className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-zinc-800"><Trash size={15} /></button>
                      </td>
                    </tr>
                    {openTasks === cp.id && (
                      <tr className="bg-zinc-950/40" data-testid={`tasks-row-${cp.id}`}>
                        <td colSpan={6} className="px-4 py-3">
                          {(!tasksMap[cp.id] || tasksMap[cp.id].length === 0) ? (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-zinc-500">Aucune échéance générée{cp.date_debut ? "" : " (définis une date de début)"}.</span>
                              <button onClick={() => generateTasks(cp.id)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid={`generate-tasks-${cp.id}`}>Générer les échéances</button>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Échéances (recalculées si la date change)</span>
                                <button onClick={() => generateTasks(cp.id)} className="text-[11px] text-zinc-400 hover:text-amber-300 flex items-center gap-1"><ArrowsClockwise size={11} /> Régénérer</button>
                              </div>
                              {tasksMap[cp.id].map((t) => (
                                <label key={t.id} className="flex items-center gap-3 text-sm cursor-pointer group" data-testid={`task-${t.id}`}>
                                  <button onClick={() => toggleTaskDone(cp.id, t)} className="shrink-0">
                                    {t.done ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <Circle size={18} className="text-zinc-600 group-hover:text-zinc-400" />}
                                  </button>
                                  <span className={`flex-1 ${t.done ? "line-through text-zinc-600" : "text-zinc-200"}`}>{t.titre}</span>
                                  <span className="text-xs font-mono text-zinc-500">{t.due_date}</span>
                                  {t.milestone === "J-45" && (
                                    <Link to="/app/marketing-studio" onClick={(e) => e.stopPropagation()} data-testid={`poster-link-${t.id}`}
                                      className="text-[11px] px-2 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30 hover:bg-violet-500/30 flex items-center gap-1">
                                      🎨 Affiche
                                    </Link>
                                  )}
                                  {t.priorite === "haute" && !t.done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D90429]/20 text-red-300 border border-[#D90429]/30">urgent</span>}
                                </label>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* Analytique — comparaison annuelle */}
        <SectionCard title={`Analytique — comparaison ${analytics?.year || year} vs ${analytics?.previous_year || year - 1}`} icon={ChartBar}>
          {(!analytics?.comparison || analytics.comparison.length === 0) ? (
            <EmptyState icon={ChartBar} title="Aucune donnée de résultats" description="Saisis les résultats d'une campagne (icône graphique) pour comparer d'une année à l'autre." />
          ) : (
            <>
              <div className="mb-6" data-testid="yoy-chart">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">
                  Inscriptions {analytics.year} vs {analytics.previous_year} — par type de campagne
                </div>
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={analytics.comparison.map((c) => ({
                        name: typeLabel(c.type),
                        [String(analytics.previous_year)]: c.previous.inscriptions,
                        [String(analytics.year)]: c.current.inscriptions,
                      }))}
                      margin={{ top: 6, right: 12, left: -12, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={54} />
                      <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, color: "#fafafa" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Legend wrapperStyle={{ fontSize: 12, color: "#d4d4d8" }} />
                      <Bar dataKey={String(analytics.previous_year)} fill="#52525b" radius={[3, 3, 0, 0]} />
                      <Bar dataKey={String(analytics.year)} fill="#D90429" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="analytics-table">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase border-b border-zinc-800">
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Inscriptions {analytics.year}</th>
                    <th className="py-2 pr-3">vs {analytics.previous_year}</th>
                    <th className="py-2 pr-3">Revenus {analytics.year}</th>
                    <th className="py-2 pr-3">vs {analytics.previous_year}</th>
                    <th className="py-2 pr-3">Conversion</th>
                    <th className="py-2 pr-3">Coût / inscr.</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.comparison.map((c) => (
                    <tr key={c.type} className="border-b border-zinc-800/60" data-testid={`analytics-row-${c.type}`}>
                      <td className="py-2.5 pr-3 text-zinc-100">{typeLabel(c.type)}</td>
                      <td className="py-2.5 pr-3 text-zinc-200 font-semibold">{c.current.inscriptions}</td>
                      <td className={`py-2.5 pr-3 font-mono ${c.delta_inscriptions > 0 ? "text-emerald-400" : c.delta_inscriptions < 0 ? "text-red-400" : "text-zinc-500"}`}>{c.delta_inscriptions > 0 ? "+" : ""}{c.delta_inscriptions} <span className="text-zinc-600">({c.previous.inscriptions})</span></td>
                      <td className="py-2.5 pr-3 text-zinc-200 font-semibold">{Number(c.current.revenus).toLocaleString("fr-CA")} $</td>
                      <td className={`py-2.5 pr-3 font-mono ${c.delta_revenus > 0 ? "text-emerald-400" : c.delta_revenus < 0 ? "text-red-400" : "text-zinc-500"}`}>{c.delta_revenus > 0 ? "+" : ""}{Number(c.delta_revenus).toLocaleString("fr-CA")} $</td>
                      <td className="py-2.5 pr-3 text-zinc-300">{c.conversion}%</td>
                      <td className="py-2.5 pr-3 text-zinc-400">{c.cout_par_inscription ? `${c.cout_par_inscription} $` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </SectionCard>

        {/* Événements à venir + À approuver */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Prochains événements" icon={Trophy}>
            {(!data?.upcoming_events || data.upcoming_events.length === 0) ? (
              <EmptyState icon={Trophy} title="Aucun événement à venir" description="Les événements dojo, Jeux Martiaux et compétitions apparaîtront ici." />
            ) : (
              <ul className="space-y-2" data-testid="upcoming-events-list">
                {data.upcoming_events.map((e, i) => (
                  <li key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-800/50 border border-zinc-800">
                    <div><div className="text-sm text-zinc-100 font-medium">{e.titre}</div><div className="text-[11px] text-zinc-500">{e.type}{e.discipline ? ` · ${e.discipline}` : ""}</div></div>
                    <span className="text-xs font-mono text-zinc-300">{e.date}</span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="À approuver & accès rapides" icon={CheckCircle}>
            <Link to="/app/pilote-reseaux" className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-colors mb-3" data-testid="approval-link">
              <span className="text-sm text-amber-200 font-semibold">Contenus en attente d'approbation</span>
              <span className="text-lg font-bold text-amber-300">{data?.approval_pending ?? 0}</span>
            </Link>
            <div className="grid grid-cols-2 gap-2">
              {[["/app/campagne-les3", "Campagne « Les 3 »"], ["/app/marketing-studio", "Marketing Studio"], ["/app/marketing-dashboard", "Tableau Marketing"], ["/app/promos", "Promos"], ["/app/mini-competitions", "Jeux Martiaux"], ["/app/marketing-plan30", "Checklist 30 jours"]].map(([to, lbl]) => (
                <Link key={to} to={to} className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-800/60 border border-zinc-700 hover:border-zinc-500 text-xs text-zinc-300 hover:text-zinc-100 transition-colors" data-testid={`quicklink-${to.split('/').pop()}`}>
                  <LinkSimple size={13} /> {lbl}
                </Link>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Modale — saisie des résultats */}
        {editRes && (
          <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="results-modal" onClick={() => setEditRes(null)}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-zinc-100 mb-1">Résultats — {editRes.nom}</h3>
              <p className="text-xs text-zinc-500 mb-4">Ces chiffres alimentent la comparaison annuelle.</p>
              <div className="grid grid-cols-2 gap-3">
                {[["prospects", "Prospects"], ["essais", "Essais"], ["inscriptions", "Inscriptions"], ["revenus", "Revenus ($)"], ["depenses", "Dépenses ($)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="block text-xs font-semibold text-zinc-400 mb-1">{l}</label>
                    <input type="number" value={editRes.resultats[k]} onChange={(e) => setRes(k, e.target.value)} className={inputCls} data-testid={`res-${k}`} />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={saveResults} data-testid="save-results" className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#D90429] text-white hover:bg-[#b8031f]">Enregistrer</button>
                <button onClick={() => setEditRes(null)} className="px-5 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700">Annuler</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
